/* The source-text pane (ADR-0045, option B).
 *
 * Two rules the look has to obey, and both come out of the decision rather than out of taste.
 *
 * It must **not** look like the document. ADR-0045: *"a panel that looks like the document while
 * being a transcription is worse than one that says 'this is the text EmitIQ read from page 3'."*
 * So no paper, no shadow, no page border, no serif body — it reads as a terminal transcript, which
 * is what it is. The caption says the same thing in words; this says it before anybody reads one.
 *
 * It must be **monospace and line-preserving**. A span is a line index and a column, so a
 * proportional font or a wrapped line would put the highlight somewhere that is not where the
 * server said it was. `white-space: pre` and a fixed advance are load-bearing, not cosmetic.
 *
 * Every colour is a token (ADR-0007, enforced by stylelint). The highlight borrows the warning
 * wash rather than inventing one: it marks *attention here*, the same job.
 */

.body {
  position: relative;
  max-height: 22rem;
  overflow: auto;
  padding: var(--gap);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel-2);
  font-family: var(--mono);
  font-size: var(--t-dense);
  line-height: 1.5;
  color: var(--ink);
}

/* One line per element, never wrapped: a wrapped line makes a column index meaningless. The
   container scrolls horizontally instead, which is honest about the text being wider than the
   panel. */
.line {
  display: block;
  white-space: pre;
}

.mark {
  padding: 0 1px;
  border-radius: 2px;
  background: var(--warn-w);
  box-shadow: inset 0 -2px 0 var(--warn);
  color: var(--ink);
}

.caption {
  margin: 0 0 var(--gap);
  font-size: var(--t-label);
  color: var(--muted);
}

.pager {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: var(--gap);
}

.pageButton {
  padding: 2px 8px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel);
  font-size: var(--t-label);
  color: var(--ink-2);
  cursor: pointer;
}

.pageButton:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 1px;
}

.pageHere {
  border-color: var(--accent-line);
  background: var(--accent-w);
  color: var(--ink);
}

.tally {
  margin: var(--gap) 0 2px;
  font-size: var(--t-label);
  color: var(--muted);
}

.answers {
  margin: 0;
  padding: 0;
  list-style: none;
}

.answer {
  display: flex;
  flex-wrap: wrap;
  gap: 4px var(--gap);
  align-items: baseline;
  padding: 6px 0;
  border-bottom: 1px solid var(--line-2);
  font-size: var(--t-dense);
}

.answerHere {
  background: var(--accent-w);
}

.answerName {
  min-width: 8rem;
  font-weight: 600;
  color: var(--ink);
}

/* The reason a field cannot be pointed at. Muted, because it is an explanation and not a warning:
   four of the five statuses are ordinary, and a page of red would teach a reader to skip them. */
.answerWhy {
  flex: 1 1 14rem;
  color: var(--muted);
}

/* A confirmed position that is not the chosen one: quiet, so the chosen one is still the answer. */
.quiet {
  padding: 0 1px;
  border-radius: 2px;
  background: transparent;
  box-shadow: inset 0 -2px 0 var(--line-2);
  color: var(--ink);
  cursor: pointer;
}

.quiet:hover {
  background: var(--accent-w);
}

.mark[role="button"] {
  cursor: pointer;
}

.mark:focus-visible,
.quiet:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 1px;
}

/* The hover: field name, value as read, confidence. Placed under the mark by the pane. */
.tip {
  position: absolute;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 4px 8px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel);
  font-size: var(--t-label);
  white-space: nowrap;
  pointer-events: none;
}
